// Geodätische Berechnungen für die Messwerkzeuge (WGS84-Ellipsoid, Karney-Algorithmen aus geographiclib):
// Entfernung, Peilung, Fläche/Umfang von Polygonen, Kreis um einen Mittelpunkt, Koordinatenformate (dezimal, Grad/Minuten, UTM, MGRS).
import { Geodesic } from 'geographiclib-geodesic'
import { forward as mgrsForward } from 'mgrs'

const geod = Geodesic.WGS84

export type Pkt = [number, number]   // [lat, lon]

/** Strecke in Metern und Anfangs-/Endpeilung (Grad, 0-360 ab Nord, im Uhrzeigersinn) */
export function strecke(a: Pkt, b: Pkt): { m: number; peilung: number; rueckpeilung: number } {
  const r = geod.Inverse(a[0], a[1], b[0], b[1])
  return { m: r.s12 ?? 0, peilung: norm360(r.azi1 ?? 0), rueckpeilung: norm360((r.azi2 ?? 0) + 180) }
}

export function norm360(g: number): number { return ((g % 360) + 360) % 360 }

/** Gesamtlänge und Teilstrecken eines Linienzugs */
export function linienzug(pkte: Pkt[]): { gesamt: number; teile: number[] } {
  const teile: number[] = []
  for (let i = 1; i < pkte.length; i++) teile.push(strecke(pkte[i - 1], pkte[i]).m)
  return { gesamt: teile.reduce((s, x) => s + x, 0), teile }
}

/** Fläche (m², positiv) und Umfang (m) eines Polygons (ab 3 Punkten, nicht geschlossen übergeben) */
export function polygon(pkte: Pkt[]): { flaeche: number; umfang: number } {
  if (pkte.length < 3) return { flaeche: 0, umfang: linienzug(pkte).gesamt }
  const p = geod.Polygon(false)
  for (const [lat, lon] of pkte) p.AddPoint(lat, lon)
  const r = p.Compute(false, true)
  return { flaeche: Math.abs(r.area ?? 0), umfang: r.perimeter ?? 0 }
}

/** Punkt in Entfernung d (m) und Richtung az (Grad) von a */
export function ziel(a: Pkt, az: number, d: number): Pkt {
  const r = geod.Direct(a[0], a[1], az, d)
  return [r.lat2 ?? a[0], r.lon2 ?? a[1]]
}

/** Kreis (Linienzug mit n Eckpunkten, geschlossen) um einen Mittelpunkt */
export function kreis(mitte: Pkt, radiusM: number, n = 72): Pkt[] {
  const ring: Pkt[] = []
  for (let i = 0; i < n; i++) ring.push(ziel(mitte, (360 * i) / n, radiusM))
  return ring
}

const ROSE = ['N', 'NNO', 'NO', 'ONO', 'O', 'OSO', 'SO', 'SSO', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']
/** Himmelsrichtung (16-teilige Windrose, deutsch) */
export function himmelsrichtung(grad: number): string { return ROSE[Math.round(norm360(grad) / 22.5) % 16] }

export function textLaenge(m: number): string {
  if (!isFinite(m)) return '–'
  if (m < 1000) return `${m.toLocaleString('de-DE', { maximumFractionDigits: m < 10 ? 2 : 1 })} m`
  return `${(m / 1000).toLocaleString('de-DE', { maximumFractionDigits: 3 })} km`
}

/** Flächengröße für Anzeigen: immer m²; ab 10.000 m² zusätzlich ha, ab 1 km² zusätzlich km² (z. B. „25.000 m² (2,5 ha)“) */
export function textFlaecheStufen(m2: number): string {
  if (!isFinite(m2) || m2 <= 0) return '–'
  const t = `${Math.round(m2).toLocaleString('de-DE')} m²`
  if (m2 < 1e4) return t
  const ha = `${(m2 / 1e4).toLocaleString('de-DE', { maximumFractionDigits: 2 })} ha`
  if (m2 < 1e6) return `${t} (${ha})`
  return `${t} (${ha} · ${(m2 / 1e6).toLocaleString('de-DE', { maximumFractionDigits: 3 })} km²)`
}

/** Fläche (m²) eines Polygons aus GeoJSON-Ring [lon, lat][] oder Punkten [lat, lon][] – geodätisch (WGS84-Ellipsoid) */
export function flaecheM2(pkte: Pkt[]): number {
  let p = pkte
  if (p.length > 1 && p[0][0] === p[p.length - 1][0] && p[0][1] === p[p.length - 1][1]) p = p.slice(0, -1)
  return p.length >= 3 ? polygon(p).flaeche : 0
}

export function textFlaeche(m2: number): { kurz: string; alle: string } {
  const m2t = `${Math.round(m2).toLocaleString('de-DE')} m²`
  const ha = `${(m2 / 1e4).toLocaleString('de-DE', { maximumFractionDigits: 2 })} ha`
  const km2 = `${(m2 / 1e6).toLocaleString('de-DE', { maximumFractionDigits: 3 })} km²`
  const kurz = m2 < 1e4 ? m2t : m2 < 1e6 ? ha : km2
  return { kurz, alle: [m2t, ha, km2].join(' = ') }
}

export function textGrad(g: number): string { return `${norm360(g).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}°` }

// --- Koordinaten ---------------------------------------------------------------------------------------------------
/** WGS84 dezimal, z. B. „52.123456, 13.123456“ (Punkt als Dezimaltrenner: maschinenlesbar, in Karten-Apps einfügbar) */
export function koordDezimal(p: Pkt): string { return `${p[0].toFixed(6)}, ${p[1].toFixed(6)}` }

/** Grad und Dezimalminuten, z. B. „N 52° 07.407′  E 013° 07.407′“ */
export function koordGradMinuten(p: Pkt): string {
  const teil = (w: number, pos: string, neg: string, stellen: number) => {
    const a = Math.abs(w)
    let g = Math.floor(a)
    let min = +((a - g) * 60).toFixed(3)
    if (min >= 60) { g += 1; min = 0 }
    return `${w >= 0 ? pos : neg} ${String(g).padStart(stellen, '0')}° ${min.toFixed(3).padStart(6, '0')}′`
  }
  return `${teil(p[0], 'N', 'S', 2)}  ${teil(p[1], 'E', 'W', 3)}`
}

/** UTM (WGS84) nach der Krüger-Reihe, Genauigkeit im Millimeterbereich. Außerhalb 80°S–84°N: null. */
export function utm(p: Pkt): { zone: number; band: string; ost: number; nord: number } | null {
  const [lat, lon] = p
  if (lat < -80 || lat > 84) return null
  let zone = Math.floor((lon + 180) / 6) + 1
  if (lat >= 56 && lat < 64 && lon >= 3 && lon < 12) zone = 32            // Südwestnorwegen
  if (lat >= 72 && lat < 84) { if (lon >= 0 && lon < 9) zone = 31; else if (lon >= 9 && lon < 21) zone = 33; else if (lon >= 21 && lon < 33) zone = 35; else if (lon >= 33 && lon < 42) zone = 37 }
  const lon0 = ((zone - 1) * 6 - 180 + 3) * Math.PI / 180
  const a = 6378137, f = 1 / 298.257223563, k0 = 0.9996
  const n = f / (2 - f)
  const A = a / (1 + n) * (1 + n ** 2 / 4 + n ** 4 / 64)
  const al = [n / 2 - 2 * n ** 2 / 3 + 5 * n ** 3 / 16, 13 * n ** 2 / 48 - 3 * n ** 3 / 5, 61 * n ** 3 / 240]
  const phi = lat * Math.PI / 180, dl = lon * Math.PI / 180 - lon0
  const e = Math.sqrt(f * (2 - f))
  const t = Math.sinh(Math.atanh(Math.sin(phi)) - e * Math.atanh(e * Math.sin(phi)))
  const xi = Math.atan2(t, Math.cos(dl)), eta = Math.atanh(Math.sin(dl) / Math.sqrt(1 + t * t))
  let x = eta, y = xi
  for (let j = 1; j <= 3; j++) {
    x += al[j - 1] * Math.cos(2 * j * xi) * Math.sinh(2 * j * eta)
    y += al[j - 1] * Math.sin(2 * j * xi) * Math.cosh(2 * j * eta)
  }
  const ost = 500000 + k0 * A * x
  const nord = (lat < 0 ? 10000000 : 0) + k0 * A * y
  const baender = 'CDEFGHJKLMNPQRSTUVWXX'
  return { zone, band: baender[Math.floor((lat + 80) / 8)], ost, nord }
}

export function koordUtm(p: Pkt): string {
  const u = utm(p)
  return u ? `${u.zone}${u.band} ${Math.round(u.ost)} ${Math.round(u.nord)}` : 'außerhalb UTM'
}

/** MGRS auf 1 m genau, mit Leerzeichen gegliedert („32U MV 12345 67890“) */
export function koordMgrs(p: Pkt): string {
  if (p[0] < -80 || p[0] > 84) return 'außerhalb MGRS'
  try {
    const s = mgrsForward([p[1], p[0]], 5)
    const m = /^(\d{1,2}[C-X])([A-Z]{2})(\d+)$/.exec(s)
    if (!m) return s
    const h = m[3].length / 2
    return `${m[1]} ${m[2]} ${m[3].slice(0, h)} ${m[3].slice(h)}`
  } catch { return 'nicht berechenbar' }
}
